/**
 * FrontispieceCover — the PRESS frontispiece title page (the scholarly
 * university-press monograph direction). A CLASSICAL CENTERED title page in the
 * tradition of a printed monograph half-title: an institution/series line in
 * small-caps accent ink, a large display-serif title in caps, a printer's-rule
 * ORNAMENT (a centred floral vector flanked by two short hairlines), an
 * italic subtitle, and a centred foot block (prepared-for / location / year in
 * Roman numerals / imprint in small-caps), all symmetric with generous top
 * padding.
 *
 * RELATION TO EditorialMastheadCover: a sibling of the editorial-monograph
 * covers and bound by the SAME data contract (`CoverProps`) + the SAME
 * conventions — title/standfirst/site resolvers from `journalCoverData`, the
 * canonical verdict from `getDisplayVerdict`, KPI chips from
 * `resolveCoverChipsForReport`, role/font tokens from `roleColor` +
 * `theme.displayFont/headingFont/bodyFont`, the COVER_MIN_HEIGHT_PX clamp (minus
 * the same 1mm epsilon that avoids the blank-orphan-page bug), and the
 * bottom-pinned beta disclaimer footer. What differs is the REGISTER: where the
 * masthead is a left-aligned journal masthead, the frontispiece is a perfectly
 * centred, symmetric, generously-spaced title page — quiet, formal, printed.
 *
 * THEME-TOKEN DRIVEN: every colour reads from the merged `theme` (background /
 * heading / body ink) or via `roleColor(theme, 'accent'|'hair'|verdictRole)`.
 * The mockup's warm-paper + oxblood palette is the DEFAULT the matching PRESS
 * preset will set — no hex is inlined here, so the cover reskins with any
 * preset. Null-safe: an empty subtitle / no prepared-for / no date / no verdict
 * each collapse their block rather than rendering a stray glyph or em-dash.
 */
import React from 'react';
import { Flower2 } from 'lucide-react';
import {
  resolveCoverTitle,
  resolveCoverKicker,
  resolveSiteWordmark,
  resolvePreparedFor,
  resolveAbstractFull,
} from '../../../../lib/reportRendering/journalCoverData';
import { resolveCoverChipsForReport } from '../../../../lib/reportRendering/coverKpiChips';
import { CoverChipField } from './CoverChipField';
import { DEFAULT_PRIMARY_COVER_LOGO_URL } from '../../../../lib/reportBranding';
import { roleColor, roleColorAlpha } from '../../../../lib/reportRoleColors';
import { reportTypographySizes, reportHeadingInk } from '../../../../lib/reportSectionHeading';
import { COVER_MIN_HEIGHT_PX } from '../../../../lib/reportCoverTokens';
import { resolveCoverVerdict, type VerdictTier } from '../../../../lib/reportVerdict';
import { REPORT_COVER_DISCLAIMER } from '../../../../lib/reportLegalDisclaimer';
import type { CoverProps } from './types';
import { ReportPlatformCredit, platformCreditToneForTheme } from '../ReportPlatformCredit';

/**
 * The resolved `disclaimer` prop defaults to {@link REPORT_COVER_DISCLAIMER}
 * (the EXACT beta caveat); the footer ALSO carries that canonical line. To avoid
 * the doubled-disclaimer defect (see EditorialMastheadCover) we render the prop
 * block ONLY when it is a DISTINCT archetype override.
 */
function isDistinctDisclaimer(disclaimer: string | undefined): disclaimer is string {
  if (typeof disclaimer !== 'string') return false;
  const trimmed = disclaimer.trim();
  return trimmed.length > 0 && trimmed !== REPORT_COVER_DISCLAIMER.trim();
}

/** Map a verdict tier to a role-colour key for the foot-block verdict line. */
function verdictStampRole(tier: VerdictTier): 'positive' | 'warning' | 'negative' | 'muted' {
  switch (tier) {
    case 'green': return 'positive';
    case 'amber': return 'warning';
    case 'red': return 'negative';
    default: return 'muted';
  }
}

/**
 * Render a Gregorian year (1..3999) in Roman numerals (e.g. 2026 → MMXXVI), the
 * monograph convention for the imprint year. Returns `null` for any non-finite /
 * out-of-range value so the foot block collapses rather than emitting garbage.
 * Pure.
 */
function toRomanNumeral(year: number): string | null {
  if (!Number.isFinite(year) || year <= 0 || year >= 4000) return null;
  const table: ReadonlyArray<readonly [number, string]> = [
    [1000, 'M'], [900, 'CM'], [500, 'D'], [400, 'CD'],
    [100, 'C'], [90, 'XC'], [50, 'L'], [40, 'XL'],
    [10, 'X'], [9, 'IX'], [5, 'V'], [4, 'IV'], [1, 'I'],
  ];
  let remaining = Math.trunc(year);
  let out = '';
  for (const [value, symbol] of table) {
    while (remaining >= value) {
      out += symbol;
      remaining -= value;
    }
  }
  return out || null;
}

/**
 * The imprint year as a Roman numeral — derived from the report's `createdAt`
 * timestamp. `null` when the timestamp is absent / unparseable, so the foot
 * block simply omits the year line rather than guessing.
 */
function resolveImprintRomanYear(createdAt: string | undefined): string | null {
  if (!createdAt?.trim()) return null;
  const parsed = new Date(createdAt);
  const year = parsed.getFullYear();
  if (!Number.isFinite(year)) return null;
  return toRomanNumeral(year);
}

export const FrontispieceCover: React.FC<CoverProps> = ({
  report, theme, branding,
  disclaimer,
}) => {
  // DESIGN PARITY NOTE: like the masthead, the frontispiece carries NO logo —
  // its identity is the small-caps series line + the imprint in the foot block.
  // `effectiveBranding` is resolved for parity with the other covers but left
  // UNRENDERED by design; `void` marks the omission explicit.
  const effectiveBranding = branding ?? {
    audienceMode: 'internal' as const,
    siteWordmark: resolveSiteWordmark(report, undefined),
    primaryLogoUrl: theme.logoUrl ?? DEFAULT_PRIMARY_COVER_LOGO_URL,
    secondaryLogoUrl: null,
    primaryLogoAlt: theme.companyName ?? 'Primary logo',
    secondaryLogoAlt: 'Customer logo',
  };
  void effectiveBranding;

  // ── Content resolvers — the SAME shared seams every cover reads ──────────
  const title = resolveCoverTitle(report);
  // The series line = the type-aware kicker ("Datacrate · BTM Feasibility").
  const seriesLine = resolveCoverKicker(theme.companyName, report);
  // The italic subtitle is the report's standfirst (cover-carried exec summary
  // lede) when present; collapses entirely when the report has no summary.
  const subtitle = resolveAbstractFull(report);
  // Foot block — prepared-for / location / year / imprint.
  const preparedFor = resolvePreparedFor(report);
  const location = report.address?.trim() || null;
  const romanYear = resolveImprintRomanYear(report.createdAt);
  const imprint = `${(theme.companyName?.trim() || 'Datacrate')} Press`;

  // Canonical COVER verdict — rendered as a small centred line in the foot block
  // ONLY when a real verdict exists (tier !== 'none'); legacy / unscored covers
  // show no stray verdict line (byte-stable). For the executive summary this
  // sources the gen-time decision-rollup band (PG-3 SSOT) so the exec cover
  // finally carries its GO/NO-GO; every other type delegates to
  // getDisplayVerdict unchanged. Deterministic chrome, not prose.
  const verdict = resolveCoverVerdict(report);
  const showVerdict = (verdict.tier !== 'none' || verdict.savedDecisionPending) && verdict.label !== '—';
  const verdictRole = verdictStampRole(verdict.tier);

  // KPI chips — the shared resolver (classic subset = the headline pair) so the
  // frontispiece's tiny key-figures footer stays unified with the other covers.
  // Null-safe: a legacy report with no metric yields em-dash chips, never a
  // throw. Suppress chips whose value is null so the centred footer stays tidy.
  const chips = resolveCoverChipsForReport(report, 'classic').filter((c) => c.value != null);

  // Body / subtitle copy size derives from the resolved body size (headingScale
  // + themeTypographyScale aware) × the theme's bodyTextScale — same density-
  // tracking as the rest of the report.
  const bodyFontSize = `calc(${reportTypographySizes(theme).body} * ${theme.bodyTextScale ?? 1})`;
  const displayFont = theme.displayFont || theme.headingFont;

  return (
    <div
      className="relative flex flex-col items-center text-center"
      data-testid="cover-frontispiece"
      data-cover-variant="frontispiece"
      style={{
        color: roleColor(theme, 'body'),
        backgroundColor: theme.backgroundColor,
        // Fill the `[data-section="cover"]` box (same printable-A4 calc the
        // masthead uses) so the paper paints edge-to-edge; the 520px floor is
        // kept via max() for the continuous viewer. flex-column + footer mt-auto
        // pins the foot block to the bottom edge.
        //
        // BLANK-ORPHAN-PAGE guard — trim by the SAME 1mm epsilon the paged deck
        // applies to the OUTER `#cover` clamp, so the bottom-pinned foot block
        // never tips the cover OVER its clamped box (which would push a near-
        // empty page-2 between the cover and §01). Mirrors EditorialMastheadCover.
        minHeight: `max(${COVER_MIN_HEIGHT_PX}px, calc(var(--report-page-height, 297mm) - 14mm - 16mm - 1mm))`,
        // Generous top padding (mockup `.fp` padding-top:34mm) so the title page
        // breathes; expressed proportionally so it scales with the cover box.
        paddingTop: 'clamp(48px, 11%, 34mm)',
      }}
    >
      {/* Series / institution line — small-caps, wide-tracked, accent ink
          (the mockup's oxblood, supplied by the preset). */}
      <div
        className="px-6"
        style={{
          fontFamily: theme.headingFont,
          fontVariant: 'small-caps',
          letterSpacing: '0.22em',
          fontSize: `calc(${bodyFontSize} * 1.05)`,
          color: roleColor(theme, 'accent'),
        }}
        data-testid="cover-frontispiece-series"
      >
        {seriesLine}
      </div>

      {/* GATEWAY POWER platform credit — top-right corner, inside the root's
          padded top (paddingTop ≥ 48px): absolute, so it adds no height and the
          bottom-pinned foot block keeps its page-1 fit. */}
      <ReportPlatformCredit
        tone={platformCreditToneForTheme(theme)}
        color={roleColor(theme, 'muted')}
        fontFamily={theme.bodyFont}
        className="absolute top-4 right-6"
        testId="cover-frontispiece-platform-credit"
      />

      {/* Title — large display-serif caps, centred. Generous top margin (mockup
          `.fp-title` margin-top:30mm) sets it apart from the series line. */}
      <h1
        id="cover-title"
        className="px-6"
        style={{
          fontFamily: displayFont,
          color: reportHeadingInk(theme),
          fontWeight: 500,
          textTransform: 'uppercase',
          fontSize: `clamp(30px, 5.4vw, calc(${reportTypographySizes(theme).h2} * 2.4))`,
          letterSpacing: '0.04em',
          lineHeight: 1.12,
          maxWidth: '20ch',
          marginTop: 'clamp(28px, 7%, 30mm)',
        }}
        data-testid="cover-frontispiece-title"
      >
        {title}
      </h1>

      {/* Printer's-rule ORNAMENT — a centred floral vector flanked by two
          short hairlines (mockup `.fp-orn` ::before / ::after). The mark takes
          the accent ink; the flanking rules take the neutral hairline token. */}
      <div
        className="flex items-center justify-center"
        style={{ margin: 'clamp(20px, 5%, 14mm) 0', width: 'min(60mm, 70%)' }}
        data-testid="cover-frontispiece-ornament"
        aria-hidden="true"
      >
        <span
          style={{
            flex: 1,
            borderTop: `1px solid ${roleColor(theme, 'hair')}`,
            maxWidth: '18mm',
          }}
        />
        <Flower2
          data-pdf-frontispiece-ornament="true"
          style={{
            color: roleColor(theme, 'accent'),
            width: 16,
            height: 16,
            margin: '0 0.9em',
            flexShrink: 0,
          }}
          strokeWidth={1.5}
        />
        <span
          style={{
            flex: 1,
            borderTop: `1px solid ${roleColor(theme, 'hair')}`,
            maxWidth: '18mm',
          }}
        />
      </div>

      {/* Italic subtitle — the standfirst lede (mockup `.fp-sub`). Collapses
          entirely when the report has no summary so no stray block renders. */}
      {subtitle && (
        <div
          className="px-6"
          style={{
            fontFamily: displayFont,
            fontStyle: 'italic',
            fontSize: `calc(${bodyFontSize} * 1.18)`,
            color: roleColor(theme, 'body'),
            lineHeight: 1.4,
            maxWidth: '46ch',
          }}
          data-testid="cover-frontispiece-subtitle"
        >
          {subtitle}
        </div>
      )}

      {/* Foot block — centred, bottom-pinned (mockup `.fp-foot` margin-top:auto).
          Prepared-for / location / year (Roman numerals) / imprint (small-caps).
          Each line is null-safe; the optional verdict + key-figures sit above
          the imprint as a quiet instrument footer. */}
      <div
        className="mt-auto px-6 w-full flex flex-col items-center"
        style={{
          fontFamily: displayFont,
          fontSize: `calc(${bodyFontSize} * 1.02)`,
          color: roleColor(theme, 'body'),
          lineHeight: 1.7,
          paddingTop: 'clamp(24px, 6%, 24mm)',
          paddingBottom: 'clamp(12px, 3%, 6mm)',
        }}
        data-testid="cover-frontispiece-foot"
      >
        {/* Optional KPI key-figures — a single mono small-caps line of the
            headline numbers, centred and dotted. Pure layout; values are
            caller-data-derived and null-safe (suppressed entirely when empty). */}
        {chips.length > 0 && (
          <div
            className="flex flex-wrap items-baseline justify-center gap-x-6 gap-y-1 pb-3 mb-3"
            style={{ borderBottom: `1px solid ${roleColorAlpha(theme, 'ink', 0.12)}` }}
            data-testid="cover-frontispiece-kpis"
          >
            {chips.map((chip) => (
              <div key={chip.id} className="text-center" data-testid={`cover-frontispiece-chip-${chip.id}`}>
                <CoverChipField
                  chip={chip}
                  report={report}
                  theme={theme}
                  testIdSuffix={`frontispiece-${chip.id}`}
                  labelClassName="text-[9px] font-bold uppercase tracking-[0.16em]"
                  valueClassName="text-[12px] font-semibold tabular-nums"
                />
              </div>
            ))}
          </div>
        )}

        {/* Optional verdict line — small-caps, tier-coloured dot + label. */}
        {showVerdict && (
          <div
            className="inline-flex items-center gap-2 pb-2"
            data-testid="cover-frontispiece-verdict"
            data-verdict-tier={verdict.tier}
          >
            <span
              aria-hidden="true"
              style={{ width: 7, height: 7, borderRadius: '9999px', background: roleColor(theme, verdictRole) }}
            />
            <span
              style={{
                fontVariant: 'small-caps',
                letterSpacing: '0.16em',
                fontSize: `calc(${bodyFontSize} * 0.92)`,
                color: roleColor(theme, verdictRole),
                fontFamily: theme.headingFont,
              }}
              data-testid="cover-frontispiece-verdict-label"
            >
              {verdict.label}
            </span>
          </div>
        )}

        {preparedFor && (
          <div data-testid="cover-frontispiece-prepared-for">{preparedFor}</div>
        )}
        {(location || romanYear) && (
          <div data-testid="cover-frontispiece-place-year">
            {location}
            {location && romanYear && <span style={{ padding: '0 0.4em' }}>{'·'}</span>}
            {romanYear && (
              <span style={{ fontFamily: "'IBM Plex Mono', monospace", letterSpacing: '0.06em' }}>
                {romanYear}
              </span>
            )}
          </div>
        )}
        <div
          className="mt-1"
          style={{
            fontVariant: 'small-caps',
            letterSpacing: '0.12em',
            color: reportHeadingInk(theme),
          }}
          data-testid="cover-frontispiece-imprint"
        >
          {imprint}
        </div>
      </div>

      {/* CONFIDENTIAL DISCLAIMER footer — a centred, muted line. A hairline top
          rule separates it from the foot block. The resolved `disclaimer` prop
          renders above the fixed beta-workflow caveat (single source, no
          doubling). Mirrors EditorialMastheadCover verbatim in language. */}
      <div
        className="px-6 pt-3 pb-2 text-center w-full"
        style={{ borderTop: `1px solid ${roleColorAlpha(theme, 'ink', 0.12)}` }}
        data-testid="cover-frontispiece-disclaimer"
      >
        {isDistinctDisclaimer(disclaimer) && (
          <div
            className="text-[8px] leading-relaxed mx-auto max-w-xl"
            style={{ color: roleColor(theme, 'body'), opacity: 0.7, fontFamily: theme.bodyFont }}
          >
            {disclaimer}
          </div>
        )}
        <div
          className="text-[8px] leading-relaxed mt-1 mx-auto max-w-xl"
          style={{ color: roleColor(theme, 'body'), opacity: 0.7, fontFamily: theme.bodyFont }}
          data-testid="cover-frontispiece-beta-disclaimer"
        >
          Confidential. Beta workflow: directional guidance only; may contain errors.
          Independently validate all findings with qualified advisors before any decision.
        </div>
      </div>
    </div>
  );
};
